/**
 * Ranked questions (M4S-5777 defect 1, PRD X61). Spec: docs/pdlc/crosstabs-ranked.md.
 *
 * The one home of the rule: what a ranked question is, the token a rank travels in from the pivot
 * to the compute, and what the extra rank tables are called. `ranked.guard.test.ts` fails on a
 * second copy of any of these outside this file.
 *
 * - **Detection.** The survey portal marks a ranked question with `question_config.multiple = '2'`
 *   on a multiple-choice question ('1' multi-select, '3' or absent single). `sub_type` is on no
 *   portal question (M4S-5562); it stays as a fallback for any other producer of these rows. The
 *   mapper calls the question type `'ranked_order'`. The classic tool and the unused Snowflake
 *   repository spell it `'ranked order'`, which `isRankedQuestion` also accepts.
 * - **The answer.** A ranking is read as one `answer_value` holding the item codes in rank order
 *   (`3:1:2` means item 3 first, item 1 second, item 2 third), split on `[|:,]` like any other
 *   answer. A short ranking ("rank your top 3") holds fewer codes than the list has items. That
 *   format is read from code, not yet from live rows (spec §1.3, live check L1), so
 *   `rankTokensOfAnswers` gives no ranks to an answer that does not fit it (more than one answer
 *   row, more codes than items, a code twice): a bad shape shows as a smaller base, never as
 *   plausible wrong ranks.
 * - **The token.** The pivot keeps every plain code (so filters, variables and weighting keep
 *   meaning "ranked at all") and, for a ranked wave only, adds one rank token per code:
 *   `R<rank>~<code>`. It starts with a letter on purpose: `parseFloat('R1~3')` is NaN, where
 *   `parseFloat('3~R1')` would be 3 and leak the code into a Mean, a Median or a numeric compare.
 *   It holds no `_`, so it can never read as a matrix cell (`matrixCellId`), and no `:`, `|` or `,`,
 *   the answer delimiters.
 *
 * Pure: no app types, no I/O.
 */

/** The question type the Postgres mapper gives a ranked question. */
export const RANKED_ORDER_TYPE = 'ranked_order';

/** The classic tool's and the Snowflake repository's spelling, accepted on read only. */
const LEGACY_RANKED_TYPE = 'ranked order';

/** The `question_config.multiple` value that marks a ranked question. */
const RANKED_MULTIPLE = '2';

/** The `sub_type` fallback, normalised (upper case, spaces as underscores). */
const RANKED_SUB_TYPE = 'RANKED_ORDER';

/**
 * Whether a portal question config marks a ranked question: `multiple` is 2 (as text or number),
 * else `sub_type` / `subType` is RANKED_ORDER. Takes the raw JSON text or the parsed object; a
 * config that does not parse is not ranked.
 */
export function isRankedConfig(raw: string | Record<string, unknown> | null | undefined): boolean {
    let config: Record<string, unknown> | null = null;
    if (typeof raw === 'string') {
        if (!raw.trim()) return false;
        try {
            const parsed: unknown = JSON.parse(raw);
            config =
                parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
        } catch {
            return false;
        }
    } else if (raw && typeof raw === 'object') {
        config = raw;
    }
    if (!config) return false;
    if (String(config.multiple ?? '').trim() === RANKED_MULTIPLE) return true;
    const subType = String(config.sub_type ?? config.subType ?? '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_');
    return subType === RANKED_SUB_TYPE;
}

/** Whether a question (a DTO, a block, an export row) is ranked, by its type. */
export function isRankedQuestion(q: { questionType?: string | null } | null | undefined): boolean {
    const type = q?.questionType;
    return type === RANKED_ORDER_TYPE || type === LEGACY_RANKED_TYPE;
}

// ── The token ────────────────────────────────────────────────────────────────

const RANK_TOKEN_RE = /^R(\d+)~(.+)$/;

/** The token a respondent holds for `code` ranked at position `rank` (1 = first). */
export function rankToken(code: string, rank: number): string {
    return `R${rank}~${code.toUpperCase()}`;
}

/** The rank and code a token holds, or null for any other token. */
export function parseRankToken(token: string): { rank: number; code: string } | null {
    const m = RANK_TOKEN_RE.exec(token);
    if (!m) return null;
    const rank = Number(m[1]);
    return rank >= 1 ? { rank, code: m[2] } : null;
}

export function isRankToken(token: string): boolean {
    return parseRankToken(token) !== null;
}

/**
 * The rank tokens one respondent's answer to a ranked question gives, or none when the answer does
 * not have the shape a ranking is read in (spec §1.3). `answers` holds one entry per answer row the
 * respondent has for the question, each the row's non-empty parts in answer order, upper-cased, as
 * the pivot reads them. The rank is a code's place among its row's parts.
 *
 * No ranks, so the respondent is outside every rank table's base, when:
 * - there is not exactly one answer row (two rows would be numbered against each other, and a
 *   format with one row per ranked item would hand every item rank 1);
 * - the row holds more codes than the question has items (`itemCount`);
 * - a code appears twice (a ranking places each item once).
 * The plain codes are untouched either way: filters and variables still read "ranked at all".
 */
export function rankTokensOfAnswers(
    answers: readonly (readonly string[])[],
    itemCount: number,
): string[] {
    if (answers.length !== 1) return [];
    const parts = answers[0].map((p) => p.toUpperCase());
    if (parts.length === 0 || parts.length > itemCount) return [];
    if (new Set(parts).size !== parts.length) return [];
    return parts.map((code, i) => rankToken(code, i + 1));
}

/** The tokens a ranking of `codes` (in rank order) gives, as the pivot emits them: plain, then ranked. */
export function rankingTokens(codes: string[]): string[] {
    const plain = codes.map((c) => c.toUpperCase());
    return [...plain, ...plain.map((code, i) => rankToken(code, i + 1))];
}

/** code → its rank, from a respondent's tokens. A code ranked twice keeps its best rank. */
export function rankPositions(tokens: Iterable<string>): Map<string, number> {
    const out = new Map<string, number>();
    for (const token of tokens) {
        const parsed = parseRankToken(token);
        if (!parsed) continue;
        const prev = out.get(parsed.code);
        if (prev === undefined || parsed.rank < prev) out.set(parsed.code, parsed.rank);
    }
    return out;
}

/** The deepest rank a respondent's tokens hold, 0 when they hold none. */
export function deepestRank(tokens: Iterable<string>): number {
    let max = 0;
    for (const token of tokens) {
        const parsed = parseRankToken(token);
        if (parsed && parsed.rank > max) max = parsed.rank;
    }
    return max;
}

/** True when a respondent's tokens hold any rank: the base of every rank table. */
export function hasRanking(tokens: Iterable<string> | null | undefined): boolean {
    if (!tokens) return false;
    for (const token of tokens) if (isRankToken(token)) return true;
    return false;
}

// ── Which waves are ranked (R4) ──────────────────────────────────────────────

/**
 * The (survey, response column) pairs whose answers are rankings, each with the number of items its
 * question ranks: `${surveyId}:${COLUMN}` → item count.
 */
export type RankedPivot = Record<string, number>;

/**
 * Every wave a ranked question was ranked in, with its item count: the pivot adds rank tokens for
 * those (survey, response column) pairs only, and the count bounds a ranking's length
 * (`rankTokensOfAnswers`). A QID ranked in one wave and not another follows each wave's own config
 * (decision R4). Built from the mapped questions the caller already holds, so the pivot never reads
 * question_config itself. Two questions on one pair (never expected) keep the larger count.
 */
export function rankedPivot(
    questions: readonly {
        qid: string;
        responseColumnId?: string;
        rankedSurveyIds?: string[];
        options?: readonly unknown[];
    }[],
): RankedPivot {
    const out = new Map<string, number>();
    for (const q of questions) {
        const column = (q.responseColumnId ?? q.qid).toUpperCase();
        const items = q.options?.length ?? 0;
        for (const sid of q.rankedSurveyIds ?? []) {
            const key = rankedPivotKey(sid, column);
            out.set(key, Math.max(out.get(key) ?? 0, items));
        }
    }
    return Object.fromEntries([...out].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
}

/** One (survey, response column) pair, as `rankedPivot` writes it and the pivot SQL compares it. */
export function rankedPivotKey(surveyId: string | number, column: string): string {
    return `${String(surveyId)}:${column.toUpperCase()}`;
}

// ── The rank tables ──────────────────────────────────────────────────────────

/**
 * Which table of a ranked question a block is (decision R2):
 * - `first`: the Rank 1 Summary, the question's own block (the one readers keyed by code see);
 * - `position`: the share who ranked each item at one position, 2 and on;
 * - `full`: the Full Rank Summary, every item × every rank with a mean rank per item.
 */
export type RankTable =
    | { table: 'first' }
    | { table: 'position'; position: number }
    | { table: 'full' };

export const RANK_1_SUMMARY: RankTable = { table: 'first' };
export const FULL_RANK_SUMMARY: RankTable = { table: 'full' };

/** A row label for one rank position: "Rank 1", "Rank 2", … */
export function rankLabel(position: number): string {
    return `Rank ${position}`;
}

/** The label of a Full Rank Summary's mean-rank row. */
export const MEAN_RANK_LABEL = 'Mean rank';

/** What a rank table is called, by itself: "Rank 1 Summary", "Rank 2", "Full Rank Summary". */
export function rankLeadLabel(mark: RankTable): string {
    if (mark.table === 'first') return `${rankLabel(1)} Summary`;
    if (mark.table === 'position') return rankLabel(mark.position);
    return 'Full Rank Summary';
}

/** True for a rank table that is a second table for its question (not the Rank 1 Summary). */
export function isExtraRankTable(mark: RankTable | null | undefined): boolean {
    return !!mark && mark.table !== 'first';
}

/** A rank table's title: the question's text, then " - " and the table's name. */
export function rankTableTitle(question: string, mark: RankTable): string {
    return `${question} - ${rankLeadLabel(mark)}`;
}

const RANK_TITLE_RE = /\s[-–—]+\s*(?:(Rank 1 Summary)|Rank (\d+)|(Full Rank Summary))\s*$/i;

/**
 * The rank table a header names by its end, or null for any other header. Reads Studio's export
 * ("QRANK — <question> - Rank 2") and is anchored at the end, like `summaryBoxOfTitle`.
 */
export function rankTableOfTitle(header: string): RankTable | null {
    const m = RANK_TITLE_RE.exec(header);
    if (!m) return null;
    if (m[1]) return RANK_1_SUMMARY;
    if (m[3]) return FULL_RANK_SUMMARY;
    const position = Number(m[2]);
    if (position === 1) return RANK_1_SUMMARY;
    return position >= 2 ? { table: 'position', position } : null;
}

/** The question's text without a rank table's suffix, or the title as it was. */
export function rankTitleQuestion(title: string, mark: RankTable): string {
    const suffix = ` - ${rankLeadLabel(mark)}`;
    return title.endsWith(suffix) ? title.slice(0, -suffix.length) : title;
}

/**
 * The tab of an extra rank table in the one-tab-per-question export: "QRANK Rank 2", "QRANK Full
 * Rank". The code is cut to 15 characters so a " (2)" repeat still fits Excel's 31. The Rank 1
 * Summary keeps its question's plain tab.
 */
export function rankTabName(qid: string, mark: RankTable): string {
    const code = qid.slice(0, 15).trimEnd();
    return mark.table === 'full'
        ? `${code} Full Rank`
        : `${code} ${rankLabel(mark.table === 'position' ? mark.position : 1)}`;
}
